import React, { useRef, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ArrowsLeftRight, CheckCircle, WarningCircle, VideoCamera, User, UploadSimple } from "@phosphor-icons/react";
import { remapCharacter, createCharacter, uploadBaseImage, fileUrl } from "@/lib/api";
import { toast } from "sonner";

function mappingState(name, characters) {
  const match = characters.find((c) => c.name.toLowerCase() === name.toLowerCase());
  if (!match) return { state: "unmapped", character: null };
  // A Main Character's base reference image is a full lock on its own — it needs no outfit at all.
  if (match.base_image_path) return { state: "locked", character: match };
  const outfit = (match.outfits || [])[0];
  if (!outfit) return match.is_main ? { state: "partial", character: match } : { state: "unmapped", character: match };
  if (!outfit.image_paths || outfit.image_paths.length === 0) return { state: "partial", character: match };
  return { state: "locked", character: match };
}

const BADGE_STYLES = {
  locked: "text-emerald-400 bg-emerald-400/10 border-emerald-400/20",
  partial: "text-amber-400 bg-amber-400/10 border-amber-400/20",
  unmapped: "text-rose-500 bg-rose-500/10 border-rose-500/20",
  camera: "text-cyan-400 bg-cyan-400/10 border-cyan-400/20",
};

const BADGE_LABELS = {
  locked: "LOCKED",
  partial: "PARTIAL — NO IMAGE",
  unmapped: "UNMAPPED",
  camera: "CAMERA — NO LOCK NEEDED",
};

function RemapDialog({ character, onRemapped }) {
  const [open, setOpen] = useState(false);
  const [newName, setNewName] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!newName.trim()) return;
    setSaving(true);
    try {
      const updated = await remapCharacter(character.id, newName.trim());
      toast.success(`Remapped "${character.name}" → "${updated.name}"`);
      setOpen(false);
      setNewName("");
      onRemapped();
    } catch (e) {
      toast.error("Remap failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <button
        data-testid={`remap-character-btn-${character.id}`}
        onClick={() => setOpen(true)}
        className="flex items-center gap-1 text-[10px] font-mono uppercase tracking-widest text-slate-400 hover:text-blue-400 transition-colors border border-slate-700 rounded-sm px-1.5 py-0.5"
      >
        <ArrowsLeftRight size={10} /> Remap
      </button>
      <DialogContent className="bg-slate-900 border-slate-800 max-w-md">
        <DialogHeader>
          <DialogTitle className="font-heading uppercase tracking-wider">
            Remap "{character.name}"
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <p className="text-xs text-slate-500">
            Point this detected character at an existing canon character's identity. Every shot referencing
            "{character.name}" will be updated to the new name — then lock its real reference set in Character Lock.
          </p>
          <div className="space-y-1.5">
            <Label className="text-xs text-slate-400 font-mono uppercase tracking-wider">New Character Name</Label>
            <Input
              data-testid="remap-new-name-input"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              placeholder="e.g. Shy Kefla"
              className="bg-slate-950 border-slate-800 rounded-sm"
            />
          </div>
        </div>
        <DialogFooter>
          <Button data-testid="remap-submit-btn" onClick={submit} disabled={saving} className="bg-blue-600 hover:bg-blue-500 rounded-sm w-full">
            {saving ? "Remapping…" : "Remap Character"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function LockImageDialog({ storyId, character, onLocked }) {
  const [open, setOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);
  const [pendingFile, setPendingFile] = useState(null);
  const alreadyLocked = !!character.base_image_path;
  const actionLabel = alreadyLocked ? "Change Base Image" : "Lock Base Image";

  const pickFile = (e) => {
    const file = e.target.files[0];
    if (file) setPendingFile(file);
  };

  const submit = async () => {
    if (!pendingFile) return;
    setUploading(true);
    try {
      // Promotes this detected character to a Main Character (upserts by name — safe to call even
      // if it already exists) so its base image is used as the top-priority reference in every shot.
      // Re-uploading on an already-locked character simply replaces base_image_path (single-value
      // field, not a list) — this is how swapping the picture during remap works.
      const mainChar = await createCharacter(storyId, character.name, true);
      const updated = await uploadBaseImage(mainChar.id, pendingFile);
      toast.success(alreadyLocked ? `Base image updated for "${character.name}"` : `Base image locked for "${character.name}"`);
      setOpen(false);
      setPendingFile(null);
      onLocked(updated);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save base image");
    } finally {
      setUploading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <button
        data-testid={`lock-base-image-btn-${character.id}`}
        onClick={() => setOpen(true)}
        className="flex items-center gap-1 text-[10px] font-mono uppercase tracking-widest text-slate-400 hover:text-fuchsia-400 transition-colors border border-slate-700 rounded-sm px-1.5 py-0.5"
      >
        <User size={10} /> {actionLabel}
      </button>
      <DialogContent className="bg-slate-900 border-slate-800 max-w-md">
        <DialogHeader>
          <DialogTitle className="font-heading uppercase tracking-wider">
            {actionLabel} — "{character.name}"
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <p className="text-xs text-slate-500">
            {alreadyLocked
              ? `This will replace "${character.name}"'s current base reference image — used as the top-priority reference in every shot featuring them.`
              : `Selecting a picture here promotes "${character.name}" to a Main Character (max 2 per story) — this image becomes the top-priority reference used in every shot featuring them.`}
          </p>
          {alreadyLocked && !pendingFile && (
            <img src={fileUrl(character.base_image_path)} alt="current" className="w-full h-24 object-contain rounded-sm border border-slate-800 opacity-70" />
          )}
          <button
            data-testid={`lock-base-image-picker-${character.id}`}
            onClick={() => fileInputRef.current.click()}
            className="w-full h-32 flex flex-col items-center justify-center gap-2 border border-dashed border-slate-700 rounded-sm text-slate-500 hover:text-fuchsia-400 hover:border-fuchsia-500/50 transition-colors overflow-hidden"
          >
            {pendingFile ? (
              <img src={URL.createObjectURL(pendingFile)} alt="preview" className="h-full object-contain" />
            ) : (
              <>
                <UploadSimple size={20} />
                <span className="text-xs">Click to choose {alreadyLocked ? "a replacement" : "an"} image</span>
              </>
            )}
          </button>
          <input ref={fileInputRef} type="file" accept="image/*" className="hidden" onChange={pickFile} />
        </div>
        <DialogFooter>
          <Button
            data-testid={`lock-base-image-submit-${character.id}`}
            onClick={submit}
            disabled={uploading || !pendingFile}
            className="bg-fuchsia-600 hover:bg-fuchsia-500 rounded-sm w-full"
          >
            {uploading ? "Saving…" : actionLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function CharacterMappingBar({ storyId, shots, characters, viewerMode, isLocked, onRemapped }) {
  const names = Array.from(new Set(shots.flatMap((s) => s.characters)));
  if (names.length === 0) return null;

  return (
    <div data-testid="character-mapping-bar" className="border border-slate-800 rounded-sm p-3 bg-slate-900/50 space-y-2">
      <h3 className="font-heading uppercase tracking-wider text-xs font-semibold text-slate-400">Characters Detected</h3>
      <div className="flex flex-wrap gap-2">
        {names.map((name) => {
          if (name === "VIEWER" && viewerMode === "POV_ONLY") {
            return (
              <span key={name} data-testid={`char-mapping-viewer-camera`} className={`flex items-center gap-1.5 text-xs px-2 py-1 rounded-sm border ${BADGE_STYLES.camera}`}>
                <VideoCamera size={12} />
                VIEWER
                <span className="font-mono text-[9px] uppercase tracking-widest opacity-80">{BADGE_LABELS.camera}</span>
              </span>
            );
          }
          const { state, character } = mappingState(name, characters);
          const Icon = state === "locked" ? CheckCircle : WarningCircle;
          return (
            <span key={name} data-testid={`char-mapping-${name}`} className={`flex items-center gap-1.5 text-xs px-2 py-1 rounded-sm border ${BADGE_STYLES[state]}`}>
              {character?.base_image_path ? (
                <img
                  data-testid={`char-mapping-thumbnail-${name}`}
                  src={fileUrl(character.base_image_path)}
                  alt={name}
                  className="w-5 h-5 rounded-full object-cover border border-current shrink-0"
                />
              ) : (
                <Icon size={12} />
              )}
              {name}
              <span className="font-mono text-[9px] uppercase tracking-widest opacity-80">{BADGE_LABELS[state]}</span>
              {character && !isLocked && <LockImageDialog storyId={storyId} character={character} onLocked={onRemapped} />}
              {character && !isLocked && <RemapDialog character={character} onRemapped={onRemapped} />}
            </span>
          );
        })}
      </div>
    </div>
  );
}
